import { useState } from "react";
import { toast } from "sonner";
import { ConfirmButton } from "./ConfirmButton";

const STATUS_META = {
  on_track: { label: "On track", color: "#4ADE80" },
  at_risk: { label: "At risk", color: "#F59E0B" },
  blocked: { label: "Blocked", color: "#FF5C5C" },
  done: { label: "Done", color: "#D6D6D6" },
};

export const ProjectsView = ({ data, api, authHeaders, onSaved }) => {
  const [form, setForm] = useState({ name: "", client_name: "", phase: "" });
  const [taskDrafts, setTaskDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const projects = data?.projects || [];
  const jsonHeaders = { ...authHeaders, "Content-Type": "application/json" };

  const call = async (method, path, body) => {
    const res = await fetch(`${api}${path}`, { method, headers: jsonHeaders, body: body ? JSON.stringify(body) : undefined });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(typeof d.detail === "string" ? d.detail : "That didn't go through");
    }
    return res.json();
  };

  const createProject = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await call("POST", "/admin/projects", { name: form.name, client_name: form.client_name || null, phase: form.phase || null });
      setForm({ name: "", client_name: "", phase: "" });
      toast.success("Project added to the board.");
      onSaved();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const update = async (id, upd) => {
    try { await call("PUT", `/admin/projects/${id}`, upd); onSaved(); } catch (err) { toast.error(err.message); }
  };

  const removeProject = async (id) => {
    try { await call("DELETE", `/admin/projects/${id}`); toast.success("Project removed."); onSaved(); } catch (err) { toast.error(err.message); }
  };

  const addTask = async (id) => {
    const draft = taskDrafts[id];
    if (!draft?.title?.trim()) return;
    try {
      await call("POST", `/admin/projects/${id}/tasks`, { title: draft.title, due: draft.due || null });
      setTaskDrafts((d) => ({ ...d, [id]: { title: "", due: "" } }));
      onSaved();
    } catch (err) { toast.error(err.message); }
  };

  const field = "rounded-xl bg-[#050505] border border-[#2E2E2E] px-3.5 py-2.5 text-[15px] font-medium text-white placeholder:text-[#D6D6D6] outline-none focus:border-[#FF5C5C] transition-colors duration-150";

  return (
    <div className="space-y-6" data-testid="projects-view">
      <form onSubmit={createProject} className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-5 flex flex-wrap items-end gap-3" data-testid="project-create-form">
        <div className="flex items-center gap-2 w-full mb-1">
          <p className="text-base font-bold text-white">Project board</p>
          <p className="text-[15px] font-medium text-[#D6D6D6] ml-2">Manage it here, or talk to the agent with your owner code</p>
        </div>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Project name *" className={`${field} flex-1 min-w-[160px]`} data-testid="project-name-input" />
        <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Client" className={`${field} w-40`} data-testid="project-client-input" />
        <input value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} placeholder="Phase" className={`${field} w-40`} data-testid="project-phase-input" />
        <button type="submit" disabled={busy || !form.name.trim()} data-testid="project-create-button"
          className="flex items-center gap-2 text-[15px] font-semibold rounded-xl bg-white text-black px-5 py-2.5 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
          Add
        </button>
      </form>

      {projects.length === 0 && (
        <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-10 text-center" data-testid="projects-empty">
          <p className="text-base font-medium text-[#D6D6D6]">The board is empty. Add a project above — or tell the agent on your owner line.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {projects.map((p) => {
          const meta = STATUS_META[p.status] || STATUS_META.on_track;
          const draft = taskDrafts[p.id] || { title: "", due: "" };
          const open = (p.tasks || []).filter((t) => !t.done).length;
          return (
            <div key={p.id} className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-5 space-y-4" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-base font-bold text-white">{p.name}</p>
                  <p className="text-[15px] font-medium text-[#D6D6D6] mt-0.5">
                    {p.client_name || "No client"} {p.phase ? `· ${p.phase}` : ""} · {open} open task{open === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={p.status} onChange={(e) => update(p.id, { status: e.target.value })} data-testid="project-status-select"
                    className="rounded-lg bg-[#050505] border border-[#2E2E2E] text-[15px] font-semibold px-2.5 py-2 outline-none"
                    style={{ color: meta.color }}>
                    {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k} style={{ color: "#fff" }}>{v.label}</option>)}
                  </select>
                  <ConfirmButton onConfirm={() => removeProject(p.id)} testId="project-delete-button" confirmLabel="Sure? Delete it"
                    className="p-2 text-[15px] font-semibold text-[#D6D6D6] hover:text-[#FF5C5C] transition-colors duration-150"
                    confirmClassName="p-2 text-[15px] font-semibold text-[#FF5C5C] whitespace-nowrap">
                    Delete
                  </ConfirmButton>
                </div>
              </div>

              <div className="space-y-2">
                {(p.tasks || []).map((t) => (
                  <div key={t.id} className="flex items-center gap-2.5 group" data-testid={`task-row-${t.id}`}>
                    <input type="checkbox" checked={t.done} onChange={() => call("PUT", `/admin/projects/${p.id}/tasks/${t.id}`, { done: !t.done }).then(onSaved).catch(() => {})}
                      className="accent-[#DC2626] w-4 h-4" data-testid="task-toggle" />
                    <span className={`text-[15px] font-medium flex-1 ${t.done ? "text-[#D6D6D6] line-through" : "text-[#D6D6D6]"}`}>{t.title}</span>
                    {t.due && <span className="text-[14px] font-medium text-[#D6D6D6]">{t.due}</span>}
                    <ConfirmButton onConfirm={() => call("DELETE", `/admin/projects/${p.id}/tasks/${t.id}`).then(onSaved).catch(() => {})}
                      testId="task-delete" confirmLabel="Sure?"
                      className="opacity-0 group-hover:opacity-100 p-1 text-[14px] font-semibold text-[#D6D6D6] hover:text-[#FF5C5C] transition-opacity duration-150"
                      confirmClassName="p-1 text-[15px] font-semibold text-[#FF5C5C]">
                      Delete
                    </ConfirmButton>
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <input value={draft.title} onChange={(e) => setTaskDrafts((d) => ({ ...d, [p.id]: { ...draft, title: e.target.value } }))}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTask(p.id))}
                    placeholder="Add a task" className={`${field} flex-1`} data-testid="task-title-input" />
                  <input type="date" value={draft.due} onChange={(e) => setTaskDrafts((d) => ({ ...d, [p.id]: { ...draft, due: e.target.value } }))}
                    className={`${field} w-36`} data-testid="task-due-input" />
                  <button onClick={() => addTask(p.id)} type="button" data-testid="task-add-button" aria-label="Add task"
                    className="px-3 rounded-xl border border-[#2E2E2E] text-[15px] font-semibold text-[#D6D6D6] hover:bg-white hover:text-black transition-colors duration-150">
                    Add
                  </button>
                </div>
              </div>

              <textarea
                defaultValue={p.notes || ""}
                onBlur={(e) => e.target.value !== (p.notes || "") && update(p.id, { notes: e.target.value })}
                placeholder="Owner notes..."
                rows={2}
                className={`${field} w-full resize-none`}
                data-testid="project-notes-input"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
